/**
 * Passkey List Component
 * @description 등록된 Passkey 목록 표시 컴포넌트
 */

import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
} from '@shared/ui/primitives';
import { Trash2, Key } from 'lucide-react';
import { usePasskey } from '@/hooks/usePasskey';
import type { Passkey } from '@/services/passkeyApi';

interface PasskeyListProps {
  onRefresh?: () => void;
  refreshTrigger?: number; // 새로고침 트리거
  adminUserId?: string; // 현재 로그인한 관리자 ID (표시용)
}

export const PasskeyList: React.FC<PasskeyListProps> = ({ onRefresh, refreshTrigger, adminUserId }) => {
  const { fetchPasskeys, remove, isLoading, error } = usePasskey();
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);

  useEffect(() => {
    loadPasskeys();
  }, [refreshTrigger]);

  const loadPasskeys = async () => {
    try {
      const data = await fetchPasskeys();
      setPasskeys(data);
    } catch (err) {
      console.error('Failed to load passkeys:', err);
    }
  };

  const handleDelete = async (passkeyId: string) => {
    if (!confirm('정말 이 Passkey를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await remove(passkeyId);
      await loadPasskeys();
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Failed to delete passkey:', err);
      alert('Passkey 삭제에 실패했습니다.');
    }
  };

  if (isLoading && passkeys.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Passkey 목록을 불러오는 중...
      </div>
    );
  }

  if (error && passkeys.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-danger mb-2">Passkey 목록을 불러오는데 실패했습니다.</p>
        <Button variant="outline" size="sm" onClick={loadPasskeys}>
          다시 시도
        </Button>
      </div>
    );
  }

  if (passkeys.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>등록된 Passkey가 없습니다.</p>
        <p className="text-xs mt-1">Passkey를 등록하면 패스워드 없이 로그인할 수 있습니다.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>관리자</TableHead>
            <TableHead>기기 유형</TableHead>
            <TableHead>전송 방식</TableHead>
            <TableHead>등록일</TableHead>
            <TableHead>마지막 사용일</TableHead>
            <TableHead className="text-right">작업</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {passkeys.map((passkey) => (
            <TableRow key={passkey.id}>
              <TableCell className="font-medium">
                {adminUserId ?? '-'}
              </TableCell>
              <TableCell className="font-medium">
                {passkey.deviceType === 'singleDevice' ? '단일 기기' : '다중 기기'}
              </TableCell>
              <TableCell>
                <div className="flex gap-1 flex-wrap">
                  {passkey.transports.map((transport) => (
                    <span
                      key={transport}
                      className="text-xs px-2 py-1 bg-muted rounded"
                    >
                      {transport}
                    </span>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                {passkey.createdAt
                  ? new Date(passkey.createdAt).toLocaleString('ko-KR')
                  : '-'}
              </TableCell>
              <TableCell>
                {passkey.lastUsedAt
                  ? new Date(passkey.lastUsedAt).toLocaleString('ko-KR')
                  : '사용 안 함'}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(passkey.id)}
                  disabled={isLoading}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
